import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { signupInputSchema, PRODUCT_NAME, type SignupInput } from "@repo/shared";
import { useTranslation } from "react-i18next";

import { AuthForm, Field, Input, PasswordInput } from "@h2j-data-team/ui";

import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/_auth/signup")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  beforeLoad: ({ search }) => {
    // Sign-up is invite-only: invited guests arrive from accept-invitation with
    // their email prefilled, and the API enforces the invitation on submit.
    if (!search.email) {
      throw redirect({
        to: "/login",
        search: {
          mode: undefined,
          token: undefined,
          error: undefined,
          redirect: search.redirect,
          email: undefined,
        },
      });
    }
  },
  head: () => ({ meta: [{ title: `Sign up — ${PRODUCT_NAME}` }] }),
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { redirect, email } = Route.useSearch();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupInputSchema),
    defaultValues: { email: email ?? "" },
  });

  const onSubmit = async (data: SignupInput) => {
    const result = await authClient.signUp.email({
      name: data.name,
      email: data.email,
      password: data.password,
      callbackURL: redirect
        ? `${window.location.origin}/verify-email/confirm?redirect=${encodeURIComponent(redirect)}`
        : `${window.location.origin}/verify-email/confirm`,
    });

    if (result.error) {
      setError("root", { message: result.error.message ?? t("signUp.failed") });
      return;
    }

    navigate({ to: "/verify-email", search: { email: data.email } });
  };

  return (
    <AuthForm
      noValidate
      title={t("signUp.heading")}
      description={t("signUp.description", { productName: PRODUCT_NAME })}
      error={errors.root?.message}
      busy={isSubmitting}
      submitLabel={t("signUp.submit")}
      submittingLabel={t("signUp.creatingAccount")}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <span>
          {t("signUp.hasAccount")}{" "}
          <Link
            to="/login"
            search={{ mode: undefined, token: undefined, error: undefined, redirect, email }}
          >
            {t("signUp.signInLink")}
          </Link>
        </span>
      }
    >
      <Field id="name" label={t("nameLabel")} error={errors.name?.message}>
        <Input autoComplete="name" placeholder={t("namePlaceholder")} {...register("name")} />
      </Field>

      {/* The invitation fixes the email: the API only accepts the invited address. */}
      <Field id="email" label={t("emailLabel")} error={errors.email?.message}>
        <Input type="email" autoComplete="username" readOnly {...register("email")} />
      </Field>

      <Field id="password" label={t("passwordLabel")} error={errors.password?.message}>
        <PasswordInput
          autoComplete="new-password"
          showLabel={t("showPassword")}
          hideLabel={t("hidePassword")}
          {...register("password")}
        />
      </Field>

      <Field
        id="confirmPassword"
        label={t("signUp.confirmPasswordLabel")}
        error={errors.confirmPassword?.message}
      >
        <PasswordInput
          autoComplete="new-password"
          showLabel={t("showPassword")}
          hideLabel={t("hidePassword")}
          {...register("confirmPassword")}
        />
      </Field>
    </AuthForm>
  );
}
